import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const DocumentList = ({ documents, onDocumentAction, selectedDocuments, onDocumentSelect }) => {
  const [sortBy, setSortBy] = useState('modified');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('list');

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'FileText';
      case 'docx': case 'doc': return 'FileText';
      case 'xlsx': case 'xls': return 'Sheet';
      default: return 'File';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'current': return 'text-success';
      case 'draft': return 'text-warning';
      case 'archived': return 'text-muted-foreground';
      default: return 'text-foreground';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const sortedDocuments = [...documents]?.sort((a, b) => {
    let aValue = a?.[sortBy];
    let bValue = b?.[sortBy];
    
    if (sortBy === 'modified') {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Contract Documents</h3>
          <div className="flex items-center space-x-2">
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <Icon name="List" size={16} />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Icon name="Grid3X3" size={16} />
            </Button>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search documents..."
              className="w-full"
            />
          </div>
          <Button variant="outline" size="sm" iconName="Filter">
            Filter
          </Button>
          <Button variant="outline" size="sm" iconName="Download">
            Export
          </Button>
        </div>
      </div>
      {/* Bulk Actions */}
      {selectedDocuments?.length > 0 && (
        <div className="p-4 bg-muted/30 border-b border-border">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedDocuments?.length} document{selectedDocuments?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" iconName="Download">
                Download All
              </Button>
              <Button variant="outline" size="sm" iconName="Archive">
                Archive
              </Button>
              <Button variant="destructive" size="sm" iconName="Trash2">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Document List */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30">
            <tr>
              <th className="w-12 p-4">
                <input
                  type="checkbox"
                  className="rounded border-border"
                  onChange={(e) => {
                    if (e?.target?.checked) {
                      onDocumentSelect(documents?.map(doc => doc?.id));
                    } else {
                      onDocumentSelect([]);
                    }
                  }}
                />
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Name</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('version')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Version</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('size')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Size</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4">
                <button
                  onClick={() => handleSort('modified')}
                  className="flex items-center space-x-1 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Modified</span>
                  <Icon name="ArrowUpDown" size={12} />
                </button>
              </th>
              <th className="text-left p-4">
                <span className="text-sm font-medium text-foreground">Uploaded By</span>
              </th>
              <th className="text-right p-4">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedDocuments?.map((document) => (
              <tr key={document.id} className="border-t border-border hover:bg-muted/30 transition-legal-fast">
                <td className="p-4">
                  <input
                    type="checkbox"
                    className="rounded border-border"
                    checked={selectedDocuments?.includes(document.id)}
                    onChange={(e) => {
                      if (e?.target?.checked) {
                        onDocumentSelect([...selectedDocuments, document.id]);
                      } else {
                        onDocumentSelect(selectedDocuments?.filter(id => id !== document.id));
                      }
                    }}
                  />
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-3">
                    <Icon name={getFileIcon(document.name)} size={20} className="text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium text-foreground">{document.name}</p>
                      <p className="text-xs text-muted-foreground">{document.category}</p>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm ${getStatusColor(document.status)}`}>
                      v{document.version}
                    </span>
                    {document.status === 'current' && (
                      <span className="px-2 py-1 text-xs bg-success/10 text-success rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{formatFileSize(document.size)}</span>
                </td>
                <td className="p-4">
                  <span className="text-sm text-foreground">{document.modified}</span>
                </td>
                <td className="p-4">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-xs font-medium text-primary-foreground">
                        {document.uploadedBy?.split(' ')?.map(n => n?.[0])?.join('')}
                      </span>
                    </div>
                    <span className="text-sm text-foreground">{document.uploadedBy}</span>
                  </div>
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDocumentAction('preview', document)}
                      title="Preview"
                    >
                      <Icon name="Eye" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDocumentAction('download', document)}
                      title="Download"
                    >
                      <Icon name="Download" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDocumentAction('share', document)}
                      title="Share"
                    >
                      <Icon name="Share2" size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDocumentAction('more', document)}
                      title="More options"
                    >
                      <Icon name="MoreHorizontal" size={16} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {documents?.length === 0 && (
        <div className="p-12 text-center">
          <Icon name="FileText" size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No documents found</h3>
          <p className="text-muted-foreground mb-4">Upload your first contract document to get started</p>
          <Button variant="default" iconName="Upload" iconPosition="left">
            Upload Document
          </Button>
        </div>
      )}
    </div>
  );
};

export default DocumentList;